import type { CSSObject } from '@mui/material/styles';
import { colors, colorsDark } from './tokens';
import { auditingCssVars } from './tokens/auditing';

/**
 * Global document styles, mounted through `MuiCssBaseline.styleOverrides`.
 *
 * Owns: brand custom properties, page background, scrollbars, focus rings, links, display math,
 * shared entrance keyframes, reduced motion. Component styling stays in `theme.ts`.
 */

/** Brand surfaces — a solid brand-blue region (the side rail). Focus rings flip to gold on them. */
const BRAND_SURFACE = '[data-surface="brand"]';

/** Pressed scrollbar thumb, top stop (handoff step 9 literal). */
const ACTIVE_THUMB_TOP = '#004f91';

/** Select chevron: a 12px down-chevron in #6b6b6b, as a data URI. */
const SELECT_CHEVRON = `url("data:image/svg+xml,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='#6b6b6b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><path d='M6 9l6 6 6-6'/></svg>",
)}")`;

export function createGlobalStyles(isDark: boolean): CSSObject {
  const c = isDark ? colorsDark : colors;
  // The brand constants are NOT mode-aware: the rail stays brand blue in both modes
  // (§2.5), so they read the light palette. Only the tint wash has a dark counterpart.
  const brand = colors.primary;
  const thumbRest = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(40,60,90,0.2)';
  const thumbGradient = (from: string, to: string) => `linear-gradient(180deg, ${from}, ${to})`;

  return {
    ':root': {
      '--hp': brand.main,
      '--hd': brand[600],
      '--hg': colors.accent.gold,
      '--hn': brand[800],
      '--hl': c.primary.tint,
      ...auditingCssVars(isDark ? 'dark' : 'light'),
    },
    html: {
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
    },
    body: {
      margin: 0,
      minHeight: '100vh',
      // Layered: hairline grid (top) → soft page gradient (bottom). Grid pitch 32px.
      // Light: ink hairlines at ~3% on warm gradient. Dark: bone hairlines at ~3.5% on deep gradient.
      backgroundImage: isDark
        ? `linear-gradient(to right, rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(135deg, ${colorsDark.background.gradientStart} 0%, ${colorsDark.background.gradientEnd} 100%)`
        : `linear-gradient(to right, rgba(15,20,25,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,20,25,0.03) 1px, transparent 1px), linear-gradient(135deg, ${colors.neutral[50]} 0%, ${colors.accent.backgroundTint} 100%)`,
      backgroundSize: '32px 32px, 32px 32px, 100% 100%',
      backgroundRepeat: 'repeat, repeat, no-repeat',
      backgroundAttachment: 'fixed, fixed, fixed',
      color: c.text.primary,
    },
    // React Flow canvas chrome (RG-WI-48). The library's own dark palette is gated on a class this app never
    // sets, and that palette draws edges at #3e3e3e, below 3:1 on the dark canvas. Setting the library's
    // unsuffixed variables from our tokens wins in both modes. Only xyflow markup (the Reggie Build canvas) matches.
    '.react-flow': {
      '--xy-edge-stroke': c.text.secondary,
      '--xy-controls-button-background-color': c.background.paper,
      '--xy-controls-button-background-color-hover': c.surface.hover,
      '--xy-controls-button-color': c.text.primary,
      '--xy-controls-button-color-hover': c.text.primary,
      '--xy-controls-button-border-color': c.border.light,
      '--xy-controls-box-shadow': `0 0 0 1px ${c.text.tertiary}`,
      '--xy-handle-background-color': c.text.secondary,
      '--xy-handle-border-color': c.background.paper,
      '--xy-edge-label-background-color': c.background.paper,
      '--xy-edge-label-color': c.text.primary,
    },
    // Global strong/b weight (style guide §3.3)
    'strong, b': {
      fontWeight: 600,
    },
    // Tabular figures for financial data
    'table, [data-tabular]': {
      fontVariantNumeric: 'tabular-nums',
    },
    // Links — plain anchors only; MUI `Link` and styled anchors carry their own class and win.
    // Palette-driven so dark mode gets the brighter ramp; in light mode these are --hp / --hd exactly.
    a: {
      color: c.primary.main,
      '&:hover': { color: c.primary[600] },
    },
    // Scrollbar — "Gradient pill" (WebKit, handoff step 9). 10px, 6px track inset, 3px transparent
    // border makes the visible thumb a 4px pill that grows on hover.
    '::-webkit-scrollbar': {
      width: 10,
      height: 10,
    },
    '::-webkit-scrollbar-track': {
      background: 'transparent',
      margin: 6,
    },
    '::-webkit-scrollbar-thumb': {
      background: thumbGradient(thumbRest, thumbRest),
      backgroundClip: 'padding-box',
      border: '3px solid transparent',
      borderRadius: 10,
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: thumbGradient('var(--hp)', 'var(--hd)'),
      backgroundClip: 'padding-box',
      borderWidth: 2.5,
    },
    '::-webkit-scrollbar-thumb:active': {
      // #004f91 is the design's literal (handoff step 9); it has no palette token.
      background: thumbGradient(ACTIVE_THUMB_TOP, 'var(--hn)'),
      backgroundClip: 'padding-box',
    },
    '::-webkit-scrollbar-corner': {
      background: 'transparent',
    },
    // Firefox only. ⚠️ Chrome 121+ and Safari 18.2+ DISABLE every `::-webkit-scrollbar` rule above as
    // soon as `scrollbar-width` or `scrollbar-color` is non-auto on the same element, so declaring
    // these unconditionally (as this file did before 2026-10-01) leaves the WebKit styling dead on
    // exactly the browsers that support it. The query is true where `::-webkit-scrollbar` parses.
    '@supports not selector(::-webkit-scrollbar)': {
      '*': {
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0,103,184,.5) transparent',
      },
    },
    // Native selects (handoff step 9): no platform arrow, the design's chevron, and the spec ring.
    // MUI's Select renders a div plus a hidden <input>, so only real <select> elements match.
    select: {
      appearance: 'none',
      WebkitAppearance: 'none',
      backgroundImage: SELECT_CHEVRON,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 10px center',
      backgroundSize: '12px 12px',
      paddingRight: 28,
    },
    'select:focus-visible': {
      outline: `2px solid ${brand.main}`,
      outlineOffset: '2px',
    },
    // Display math — WI-84 RC-1. KaTeX ships NO scroller and assumes the host
    // supplies one: `katex.min.css` sets `.katex-display > .katex { white-space:
    // nowrap }` and its only 9 `overflow` declarations are all `hidden`. The app
    // supplied none, and nothing between `.katex-display` and the report card's
    // scroller bounds the x-axis, so a wide formula painted straight over the
    // accordion. The house pattern for wide content already existed and had been
    // applied to the other two classes — the data table and the code fence in
    // `MarkdownText.tsx` — and not to math.
    //
    // ⚠️ ALL THREE PARTS ARE REQUIRED; the naive one-property version is wrong.
    //   (1) `overflow-y: visible` is NOT redundant. Setting `overflow-x: auto`
    //       forces the other axis to compute to `auto` rather than `visible`, and
    //       KaTeX's tall constructs (\sum limits, nested \frac, \sqrt) sit outside
    //       the line box on struts — they WOULD CLIP. The padding is what gives
    //       them room once a scrollbar can appear.
    //   (2) inline `.katex` is a SEPARATE rule and deliberately gets no scroller:
    //       making an inline element a scroll container moves its baseline to the
    //       bottom margin edge and drops every inline formula relative to the
    //       surrounding text.
    //   (3) the container belongs on the LEAF. Do NOT move it to the segment
    //       wrapper or any ancestor — that wraps all section prose in a scroll box
    //       to solve a leaf-local problem.
    // The scrollbar thumb is visible at rest (global rules above), so a scrollable equation
    // has its overflow affordance without a math-specific override. Before 2026-10-01 thumbs were
    // transparent until hover and this block carried a rest-state override; it was removed with that.
    // ⛔ Do NOT make math wrap. KaTeX's `white-space: nowrap` is correct; a
    //    formula broken mid-expression is a WRONG formula, not a narrow one.
    '.katex-display': {
      overflowX: 'auto',
      overflowY: 'visible',
      // 🔴 6px IS A MEASUREMENT, NOT A GUESS — do not "tidy" it to 4 or 8.
      // Swept across all 18 display equations in the operator's real report,
      // with this rule injected into the live page (2026-09-02):
      //     0px -> 15/18 clipped (worst 6px)
      //     4px ->  8/18 clipped (worst 2px)
      //     5px ->  1/18 clipped (worst 1px)
      //     6px ->  0/18 clipped          <- monotone to zero, not a rounding artifact
      // The clipped constructs are exactly the ones the note above predicts:
      // \frac, \tfrac, \sqrt, \left(…\right).
      //
      // ⚠️ THE HAZARD IS CREATED BY THIS RULE, which is why the constant is
      // load-bearing rather than cosmetic. With NO rule at all, 15/18 already
      // report scrollHeight > clientHeight — and it is HARMLESS, because
      // `overflow` computes to `visible` and the glyphs simply paint outside
      // the box. Adding `overflow-x: auto` forces `overflow-y` to compute to
      // `auto` (measured), at which point that same overhang becomes a CLIP.
      // The padding is what buys it back. Under-provision here and the fix
      // trades a horizontal overflow for a vertical truncation.
      //
      // ⚠️ NOTHING IN CI CAN CATCH A REGRESSION HERE. jsdom does no layout, so
      // the vitest suite cannot measure clipping (it says so itself). If the
      // equation corpus changes materially, RE-RUN THE SWEEP in a real browser;
      // do not reason about it.
      paddingTop: 6,
      paddingBottom: 6,
      maxWidth: '100%',
      WebkitOverflowScrolling: 'touch',
    },
    // Inline math (2) — bounded, but NOT a scroll container, and the baseline is
    // stated explicitly rather than left to inherit.
    '.katex:not(.katex-display > .katex)': {
      maxWidth: '100%',
      verticalAlign: 'baseline',
    },
    // Focus ring (style guide §5.2, handoff step 1: `2px solid #0067b8`, offset 2px — no halo; the
    // 4px primaryOverlay halo this rule carried until 2026-10-01 was removed to match the spec).
    // Brand-blue surfaces: gold, because the primary ring is near-invisible on #0067b8. Mark a brand
    // region with `data-surface="brand"`.
    '*:focus-visible': {
      outline: `2px solid ${c.primary.main}`,
      outlineOffset: '2px',
    },
    [`${BRAND_SURFACE} *:focus-visible`]: {
      outline: `2px solid ${colors.accent.gold}`,
    },
    // Entrance keyframes — consumed through `theme.custom.motion.animation`.
    '@keyframes tabIn': {
      from: { opacity: 0, transform: 'translateY(8px)' },
      to: { opacity: 1, transform: 'none' },
    },
    '@keyframes projIn': {
      from: { transform: 'translateY(6px)' },
      to: { transform: 'none' },
    },
    // Auditing workspace motion (auditing refinement handoff §14) — consumed through `auditingMotion`.
    '@keyframes auditTabIn': {
      from: { opacity: 0, transform: 'translateX(12px)' },
      to: { opacity: 1, transform: 'none' },
    },
    '@keyframes auditChatIn': {
      from: { width: 0, opacity: 0 },
      to: { width: 'var(--audit-chat-width)', opacity: 1 },
    },
    '@keyframes auditChatOut': {
      from: { width: 'var(--audit-chat-width)', opacity: 1 },
      '60%': { opacity: 0 },
      to: { width: 0, opacity: 0 },
    },
    '@keyframes auditHandleIn': {
      from: { opacity: 0, transform: 'translateX(100%)' },
      to: { opacity: 1, transform: 'none' },
    },
    // Reduced motion (style guide §8.4)
    '@media (prefers-reduced-motion: reduce)': {
      '*, *::before, *::after': {
        animation: 'none !important',
        transition: 'none !important',
        scrollBehavior: 'auto !important',
      },
      // Holiday particles start at opacity 0 inside their keyframes; with the animation stopped they
      // would vanish, so they rest visibly instead.
      '.hp': { opacity: '.3 !important' },
    },
  };
}
